import { useRef, useState } from "react";
import { Slider } from "../../components/ui/slider";
import { CategoryPhoto } from "../../components/CategoryPhoto";
import { api, errText } from "../../lib/api";
import { compressImage } from "../../lib/image";
import { CATEGORY_PHOTOS } from "../../lib/categoryPhotos";
import { toast } from "sonner";
import { Crop, Image as ImageIcon, ImagePlus, Loader2, RotateCcw, Shapes } from "lucide-react";

// Per-tile choice between the round photo and the icon, with photo upload / reset to the starter photo.
export function TilePhotoControl({ c, ov, setField }) {
  const [busy, setBusy] = useState(false);
  const [crop, setCrop] = useState(false);
  const mode = ov.display || "photo";
  const src = ov.photo || CATEGORY_PHOTOS[c.id] || "";
  const onFile = async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", await compressImage(file));
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setField(c.id, "photo", data.path);
      setField(c.id, "photo_pos", "");
      setField(c.id, "photo_zoom", 100);
      setField(c.id, "display", "photo");
    } catch (err) { toast.error(errText(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid={`cat-photo-row-${c.id}`}>
      <div className="inline-flex rounded-lg bg-slate-100 p-0.5">
        {[["photo", "Photo", ImageIcon], ["icon", "Icon", Shapes]].map(([v, l, I]) => (
          <button key={v} type="button" onClick={() => setField(c.id, "display", v)} data-testid={`cat-display-${v}-${c.id}`}
            className={`h-7 px-2.5 rounded-md text-[11px] font-semibold flex items-center gap-1 transition-colors ${mode === v ? "bg-white text-[#F5551E] shadow-sm" : "text-slate-500"}`}><I size={12} /> {l}</button>
        ))}
      </div>
      {mode === "photo" && (<>
        {src && <CategoryPhoto src={src} pos={ov.photo_pos} zoom={ov.photo_zoom} className="h-9 w-9 ring-2 ring-[#FFE3D8]" data-testid={`cat-photo-thumb-${c.id}`} />}
        {src && <button type="button" onClick={() => setCrop((v) => !v)} data-testid={`cat-photo-crop-${c.id}`} className={`text-[11px] font-semibold flex items-center gap-1 hover:underline ${crop ? "text-[#12234A]" : "text-[#F5551E]"}`}><Crop size={12} /> {crop ? "Done" : "Adjust"}</button>}
        <label className="text-[11px] font-semibold text-[#F5551E] cursor-pointer hover:underline flex items-center gap-1" data-testid={`cat-photo-upload-${c.id}`}>
          {busy ? <Loader2 size={12} className="animate-spin" /> : <ImagePlus size={12} />} Replace
          <input type="file" accept="image/*" onChange={onFile} className="hidden" />
        </label>
        {ov.photo && <button type="button" onClick={() => { setField(c.id, "photo", ""); setField(c.id, "photo_pos", ""); setField(c.id, "photo_zoom", 100); }} data-testid={`cat-photo-reset-${c.id}`} className="text-[11px] text-slate-500 hover:text-[#12234A] flex items-center gap-1"><RotateCcw size={11} /> Starter photo</button>}
      </>)}
      {mode === "photo" && crop && src && <CropPicker c={c} ov={ov} src={src} setField={setField} />}
    </div>
  );
}

// Drag the photo inside the circle to choose the focus point; slider zooms in.
function CropPicker({ c, ov, src, setField }) {
  const drag = useRef(null);
  const [x, y] = (ov.photo_pos || "50% 50%").split(" ").map((v) => parseInt(v, 10));
  const zoom = ov.photo_zoom || 100;
  const clamp = (v) => Math.max(0, Math.min(100, Math.round(v)));
  const down = (e) => { e.currentTarget.setPointerCapture(e.pointerId); drag.current = { px: e.clientX, py: e.clientY, x, y }; };
  const move = (e) => {
    const d = drag.current;
    if (!d) return;
    const k = 100 / (112 * (zoom / 100));
    setField(c.id, "photo_pos", `${clamp(d.x - (e.clientX - d.px) * k)}% ${clamp(d.y - (e.clientY - d.py) * k)}%`);
  };
  return (
    <div className="basis-full mt-1 rounded-xl bg-slate-50 p-3 flex items-center gap-4" data-testid={`cat-crop-${c.id}`}>
      <div onPointerDown={down} onPointerMove={move} onPointerUp={() => { drag.current = null; }} onPointerCancel={() => { drag.current = null; }}
        className="relative h-28 w-28 shrink-0 cursor-grab active:cursor-grabbing touch-none" data-testid={`cat-crop-area-${c.id}`} title="Drag to move the photo">
        <CategoryPhoto src={src} pos={ov.photo_pos} zoom={zoom} className="h-28 w-28 ring-4 ring-white shadow" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] text-slate-500">Drag the photo to choose what shows in the circle.</p>
        <div className="mt-3 flex items-center gap-3">
          <span className="text-[11px] text-slate-500">Zoom</span>
          <Slider value={[zoom]} min={100} max={300} step={5} onValueChange={([v]) => setField(c.id, "photo_zoom", v)} className="flex-1" data-testid={`cat-crop-zoom-${c.id}`} />
          <span className="text-[11px] font-semibold w-10 text-right" data-testid={`cat-crop-zoom-value-${c.id}`}>{zoom}%</span>
        </div>
        <button type="button" onClick={() => { setField(c.id, "photo_pos", ""); setField(c.id, "photo_zoom", 100); }} data-testid={`cat-crop-reset-${c.id}`}
          className="mt-2 text-[11px] text-slate-500 hover:text-[#12234A] flex items-center gap-1"><RotateCcw size={11} /> Reset crop</button>
        <p className="text-[10px] text-slate-400 mt-1" data-testid={`cat-crop-pos-${c.id}`}>Focus {ov.photo_pos || "50% 50%"} · press Save tiles to keep it</p>
      </div>
    </div>
  );
}
